import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { apiClient } from '../services/api';
import { Assignment, AssignmentProgressItem } from '../types/api';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

interface AssignmentProgressModalProps {
  assignment: Assignment;
  folderName: string;
  onClose: () => void;
}

const scoreColor = (score: number): string => {
  if (score >= 0.67) return 'text-green-600';
  if (score >= 0.33) return 'text-yellow-600';
  return 'text-red-500';
};

export const AssignmentProgressModal: React.FC<AssignmentProgressModalProps> = ({
  assignment,
  folderName,
  onClose,
}) => {
  useBodyScrollLock();
  const [progress, setProgress] = useState<AssignmentProgressItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const data = await apiClient.getAssignmentProgress(assignment.id);
        setProgress(data.items);
      } catch {
        setError('Failed to load progress.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchProgress();
  }, [assignment.id]);

  const formatDueDate = (date: string | null): string => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-xl font-bold text-gray-900">Progress</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="text-sm text-gray-500 mb-1">{folderName}</p>
        {assignment.due_date && (
          <p className="text-xs text-gray-400 mb-4">Due {formatDueDate(assignment.due_date)}</p>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            <span className="ml-2 text-sm text-gray-500">Loading...</span>
          </div>
        ) : error ? (
          <div className="rounded-md bg-red-50 p-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        ) : progress.length === 0 ? (
          <p className="text-sm text-gray-500 py-4">No progress data yet.</p>
        ) : (
          <div className="overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 text-gray-500 font-medium">Student</th>
                  <th className="text-center py-2 text-gray-500 font-medium">Reviewed</th>
                  <th className="text-center py-2 text-gray-500 font-medium">Score</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {progress.map((item) => (
                  <tr key={item.user_id}>
                    <td className="py-3 text-gray-800">{item.name}</td>
                    <td className="py-3 text-center text-gray-600">
                      {item.cards_reviewed}/{item.cards_total}
                    </td>
                    <td className={`py-3 text-center font-medium ${scoreColor(item.avg_score)}`}>
                      {Math.round(item.avg_score * 100)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
